import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Col, Radio, Row, Statistic } from "antd";
import { Line } from "@ant-design/charts";

import sdk from "@/sdk";
import { formatInput } from "@/utils/formatters";

function DashboardWidget() {
  const [chartDays, setChartDays] = useState<7 | 30 | 0>(0);

  const { data, isLoading, isError, isFetching, refetch } = useQuery({
    queryKey: ["dashboard-data"],
    queryFn: () => sdk.getDashboardData(),
    select: (data) => data.getDashboardData,
  });
  const hasData = data != null;
  const moneyValue = (value: number | null | undefined) =>
    value == null ? "—" : formatInput({ balance: value }) + "₮";

  const chartData = useMemo(() => {
    if (!data) return [];

    const cutoff =
      chartDays > 0
        ? new Date(Date.now() - chartDays * 24 * 60 * 60 * 1000)
            .toISOString()
            .slice(0, 10)
        : undefined;

    const deposits = (data.depositChartData ?? [])
      .filter((d) => !cutoff || d.date >= cutoff)
      .map((d) => ({ date: d.date, amount: d.amount, type: "Цэнэглэлт" }));

    const bondInterest = (data.bondInterestChartData ?? [])
      .filter((d) => !cutoff || d.date >= cutoff)
      .map((d) => ({ date: d.date, amount: d.amount, type: "Бондын хүү" }));

    const withdrawals = (data.withdrawalChartData ?? [])
      .filter((d) => !cutoff || d.date >= cutoff)
      .map((d) => ({ date: d.date, amount: d.amount, type: "Буцаалт" }));

    return [...deposits, ...bondInterest, ...withdrawals];
  }, [data, chartDays]);

  return (
    <>
      {isError && (
        <Alert
          showIcon
          type={hasData ? "warning" : "error"}
          message={
            hasData
              ? "Мэдээллийг шинэчилж чадсангүй"
              : "Серверээс мэдээлэл авч чадсангүй"
          }
          description={
            hasData
              ? "Сүүлд амжилттай авсан мэдээллийг харуулж байна. Дахин ачаалж шинэчилнэ үү."
              : "Үзүүлэлтүүдийг одоогоор харуулах боломжгүй байна. Түр хүлээгээд дахин ачаална уу."
          }
          action={
            <Button loading={isFetching} onClick={() => void refetch()}>
              Дахин ачаалах
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      )}
      <Row gutter={[16, 16]}>
        <Col span={6}>
          <div className="chrat-paper">
            <Statistic
              loading={isLoading}
              title="Одоогийн нийт хөрөнгө (өдөр бүр)"
              value={moneyValue(data?.totalValue)}
            />
          </div>
        </Col>
        <Col span={6}>
          <div className="chrat-paper">
            <Statistic
              loading={isLoading}
              title="Нийт цэнэглэлт (бүх хугацаанд)"
              value={moneyValue(data?.totalInvestment)}
            />
          </div>
        </Col>
        <Col span={6}>
          <div className="chrat-paper">
            <Statistic
              loading={isLoading}
              title="Нийт хүлээгдэж буй захиалга"
              value={data?.totalWaitingOrders ?? "—"}
            />
          </div>
        </Col>
        <Col span={6}>
          <div className="chrat-paper">
            <Statistic
              loading={isLoading}
              title="Цэнэглэлт хийсэн хэрэглэгч"
              value={data?.totalUser ?? "—"}
            />
          </div>
        </Col>
      </Row>

      <div className="chrat-paper" style={{ marginTop: 16 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
          }}
        >
          <h3 style={{ margin: 0 }}>Цэнэглэлт / Буцаалт</h3>
          <Radio.Group
            value={chartDays}
            onChange={(e) => setChartDays(e.target.value)}
            optionType="button"
            buttonStyle="solid"
            size="small"
            disabled={!hasData}
          >
            <Radio.Button value={7}>7 хоног</Radio.Button>
            <Radio.Button value={30}>30 хоног</Radio.Button>
            <Radio.Button value={0}>Бүгд</Radio.Button>
          </Radio.Group>
        </div>
        {chartData.length > 0 ? (
          <Line
            data={chartData}
            xField="date"
            yField="amount"
            seriesField="type"
            height={350}
            yAxis={{
              label: {
                formatter: (v: string) =>
                  new Intl.NumberFormat().format(Number(v)) + "₮",
              },
            }}
            tooltip={{
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              formatter: (datum: any) => ({
                name: datum.type,
                value: new Intl.NumberFormat().format(datum.amount) + "₮",
              }),
            }}
          />
        ) : (
          !isLoading && (
            <div style={{ textAlign: "center", padding: 40, color: "#999" }}>
              {isError && !hasData
                ? "Графикийн мэдээллийг авч чадсангүй"
                : "Мэдээлэл байхгүй"}
            </div>
          )
        )}
      </div>
    </>
  );
}

export default DashboardWidget;
